import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  InputEventOutgoing,
  InputEventIncoming,
  WebSocketStatus,
  ButtonAction,
  TouchAction,
} from '../types/websocket';

export interface InputEventsStreamConfig {
  machineId: string;
  sessionId: string;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
}

export const useInputEventsStream = (config: InputEventsStreamConfig) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [status, setStatus] = useState<WebSocketStatus>({
    connected: false,
    error: null,
    reconnectAttempts: 0
  });

  const [lastReceivedEvent, setLastReceivedEvent] = useState<InputEventIncoming | null>(null);

  const { accessToken, refreshToken } = useAuth();

  const {
    machineId,
    sessionId,
    autoReconnect = true,
    maxReconnectAttempts = 5,
    reconnectDelay = 3000
  } = config;

  // Build WebSocket URL (new path: /input-stream)
  const buildWebSocketUrl = useCallback(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    let wsHost;
    if (window.location.hostname === 'localhost') {
      wsHost = window.location.hostname + ':8080';
    } else {
      if (window.location.hostname === 'casino-gui-dev.corde.tech') {
        wsHost = 'casino-api-dev.corde.tech';
      } else if (window.location.hostname === 'casino-gui.corde.tech') {
        wsHost = 'casino-api.corde.tech';
      } else {
        wsHost = window.location.hostname;
      }
    }

    const params = new URLSearchParams();
    if (accessToken) {
      params.append('access_token', accessToken);
    }
    params.append('machine_id', machineId);
    params.append('session_id', sessionId);

    return `${wsProtocol}//${wsHost}/ws/v1/proxy/input-stream?${params.toString()}`;
  }, [accessToken, machineId, sessionId]);

  // Generic send helper
  const sendEvent = useCallback((event: InputEventOutgoing): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected, cannot send input event');
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify(event));
      return true;
    } catch (error) {
      console.error('Failed to send input event:', error);
      setStatus(prev => ({ ...prev, error: 'Failed to send input event' }));
      return false;
    }
  }, []);

  // Send button event
  const sendButtonEvent = useCallback((buttonId: number, action: ButtonAction): boolean => {
    const event: InputEventOutgoing = {
      type: 'button',
      machine_id: machineId,
      session_id: sessionId,
      button_id: buttonId,
      button_action: action,
      timestamp: new Date().toISOString()
    };
    return sendEvent(event);
  }, [machineId, sessionId, sendEvent]);

  // Send touch event
  const sendTouchEvent = useCallback((x: number, y: number, action: TouchAction): boolean => {
    const event: InputEventOutgoing = {
      type: 'touch',
      machine_id: machineId,
      session_id: sessionId,
      x,
      y,
      touch_action: action,
      timestamp: new Date().toISOString()
    };
    return sendEvent(event);
  }, [machineId, sessionId, sendEvent]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(async () => {
    if (!accessToken) {
      console.warn('No access token available for input events stream');
      return;
    }

    const wsUrl = buildWebSocketUrl();
    console.log('Connecting to input events stream:', wsUrl);

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Input events WebSocket connected');
        setStatus(prev => ({
          ...prev,
          connected: true,
          error: null,
          reconnectAttempts: 0
        }));
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data: InputEventIncoming = JSON.parse(event.data);
          setLastReceivedEvent(data);
        } catch (error) {
          console.error('Failed to parse input event response:', error);
          setStatus(prev => ({ ...prev, error: 'Failed to parse input event response' }));
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Input events WebSocket error:', error);
        setStatus(prev => ({ ...prev, error: 'WebSocket connection error' }));
      };

      wsRef.current.onclose = async (event) => {
        console.log('Input events WebSocket closed:', event.code, event.reason);

        const isAuthError = event.code === 1011 ||
          (event.reason && event.reason.toLowerCase().includes('token')) ||
          (event.reason && event.reason.toLowerCase().includes('unauthorized'));

        setStatus(currentStatus => {
          const newStatus = { ...currentStatus, connected: false };

          if (isAuthError && currentStatus.reconnectAttempts === 0) {
            console.log('Attempting token refresh due to auth error');
            refreshToken().then((newToken) => {
              if (newToken) {
                console.log('Token refreshed, attempting reconnection');
                setTimeout(() => connectWebSocket(), 1000);
              } else {
                console.error('Token refresh failed');
                setStatus(prev => ({
                  ...prev,
                  error: 'Authentication failed - please login again'
                }));
              }
            });
            return { ...newStatus, reconnectAttempts: 1 };
          }

          if (autoReconnect && currentStatus.reconnectAttempts < maxReconnectAttempts) {
            newStatus.reconnectAttempts = currentStatus.reconnectAttempts + 1;

            reconnectTimeoutRef.current = setTimeout(() => {
              console.log(`Attempting to reconnect input events stream (${newStatus.reconnectAttempts}/${maxReconnectAttempts})`);
              connectWebSocket();
            }, reconnectDelay * newStatus.reconnectAttempts);
          }

          return newStatus;
        });
      };

    } catch (error) {
      console.error('Failed to create input events WebSocket:', error);
      setStatus(prev => ({ ...prev, error: 'Failed to create WebSocket connection' }));
    }
  }, [buildWebSocketUrl, accessToken, refreshToken, autoReconnect, maxReconnectAttempts, reconnectDelay]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    console.log('Disconnecting input events stream');

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus({
      connected: false,
      error: null,
      reconnectAttempts: 0
    });
  }, []);

  // Manual reconnect
  const reconnect = useCallback(async () => {
    disconnect();
    setTimeout(() => {
      connectWebSocket();
    }, 100);
  }, [disconnect, connectWebSocket]);

  // Connect when component mounts or dependencies change
  useEffect(() => {
    if (accessToken && machineId && sessionId) {
      connectWebSocket();
    }

    return () => {
      disconnect();
    };
  }, [accessToken, machineId, sessionId]);

  return {
    status,
    lastReceivedEvent,
    sendButtonEvent,
    sendTouchEvent,
    sendEvent,
    disconnect,
    reconnect
  };
};
